import React, { useState } from "react";
import { XCircle, LogIn, Loader2, Mail } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useAuth } from "../../context/AuthContext";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthModal = ({ onClose, onGoogleLogin, onSuccess }) => {
  const { loginWithPassword, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await loginWithPassword(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 sm:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="auth-modal">
        <div className="flex items-start justify-between">
          <h3 className="font-display text-2xl font-extrabold text-slate-900">
            {mode === "login" ? "Log in to your account" : "Create your account"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="auth-modal-close">
            <XCircle className="h-6 w-6" />
          </button>
        </div>
        <p className="mt-1 text-sm text-slate-500">View your SEO reports, unlock full audits and keep your history.</p>

        <Button onClick={onGoogleLogin} variant="outline" className="mt-6 w-full rounded-lg border-slate-300" data-testid="auth-google-button">
          <LogIn className="h-4 w-4 mr-2" /> Continue with Google
        </Button>

        <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> or with email <span className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === "register" && (
            <Input name="name" value={form.name} onChange={handleChange} placeholder="Your name" required className="rounded-lg" data-testid="auth-name-input" />
          )}
          <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@company.com" required className="rounded-lg" data-testid="auth-email-input" />
          <Input name="password" type="password" value={form.password} onChange={handleChange} placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"} required minLength={8} className="rounded-lg" data-testid="auth-password-input" />
          {error && <p className="text-sm text-rose-600" data-testid="auth-error-message">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full rounded-lg bg-primary hover:bg-primary/90" data-testid="auth-submit-button">
            {busy ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Please wait…</>) : (<><Mail className="h-4 w-4 mr-2" /> {mode === "login" ? "Log in" : "Create account"}</>)}
          </Button>
        </form>

        <p className="mt-4 text-sm text-slate-500 text-center">
          {mode === "login" ? (
            <>No account?{" "}
              <button onClick={() => { setMode("register"); setError(""); }} className="font-semibold text-primary hover:underline" data-testid="auth-switch-register">Sign up free</button>
            </>
          ) : (
            <>Already have an account?{" "}
              <button onClick={() => { setMode("login"); setError(""); }} className="font-semibold text-primary hover:underline" data-testid="auth-switch-login">Log in</button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};
